import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { LoadingScreen } from '@components';
import { useAuth } from 'react-oidc-context';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSession, getSessionStatus } from '@store/features';
import { MOCK, signoutState } from '@auth/userManager';
import { Alert, Typography } from '@esgian/esgianui';

function PrivateRoute({ children }) {
  const auth = useAuth();
  const dispatch = useDispatch();
  const sessionStatus = useSelector(getSessionStatus);

  useEffect(() => {
    // Do not start a sign-in while a logout is in flight.
    if (
      !MOCK &&
      !auth.isLoading &&
      !auth.isAuthenticated &&
      !auth.activeNavigator &&
      !signoutState.inProgress
    ) {
      // Preserve the originally requested deep link so <AuthCallback> can return here after login.
      auth.signinRedirect({
        state: { returnTo: `${window.location.pathname}${window.location.search}` }
      });
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.activeNavigator]);

  useEffect(() => {
    // Do not fetch the session while a logout is in flight: the token is already gone, so the
    // request cannot succeed and only leaves the slice stuck mid-flight.
    if ((MOCK || auth.isAuthenticated) && sessionStatus === 'idle' && !signoutState.inProgress) {
      dispatch(fetchSession());
    }
  }, [auth.isAuthenticated, sessionStatus]);

  if (MOCK) {
    if (sessionStatus === 'idle' || sessionStatus === 'loading') return <LoadingScreen />;
    if (sessionStatus === 'ok') return children;
  }

  if (auth.isLoading || (!auth.isAuthenticated && !MOCK)) return <LoadingScreen />;

  if (auth.isAuthenticated || MOCK) {
    if (sessionStatus === 'idle' || sessionStatus === 'loading') return <LoadingScreen />;

    if (sessionStatus === 'noaccess') {
      return (
        <Alert severity="error">
          <Typography variant="body1">
            This user does not have access to OX Energy Management.
          </Typography>
        </Alert>
      );
    }

    if (sessionStatus === 'error') {
      return (
        <Alert severity="error">
          <Typography variant="body1">
            An error occurred loading your session. Please refresh the page.
          </Typography>
        </Alert>
      );
    }

    if (sessionStatus === 'ok') return children;
  }

  return <LoadingScreen />;
}

PrivateRoute.propTypes = {
  children: PropTypes.node.isRequired
};

PrivateRoute.defaultProps = {};

export default PrivateRoute;
